<template>
  <div :class="$style['form-date']">
    <div v-if="label" :class="$style['form-date__label']">
      {{ label }}
      <small>{{ sublabel }}</small>
    </div>
    <div :class="$style['form-date__inputs']">
      <FormInput
        ref="dayElm"
        v-model="day"
        data-test-id="date-day"
        type="tel"
        min="1"
        max="31"
        maxlength="2"
        pattern="[0-9]{2}"
        :label="$t('general/placeholder-day')"
        @focus="onFocus"
        @blur="onBlur" />
      <FormInput
        ref="monthElm"
        v-model="month"
        data-test-id="date-month"
        type="tel"
        min="1"
        max="12"
        maxlength="2"
        pattern="[0-9]{2}"
        :label="$t('general/placeholder-month')"
        @focus="onFocus"
        @blur="onBlur" />
      <FormInput
        ref="yearElm"
        v-model="year"
        data-test-id="date-year"
        type="tel"
        pattern="[0-9]{4}"
        maxlength="4"
        :label="$t('general/placeholder-year')"
        @focus="onFocus"
        @blur="onBlur" />

      <svg
        :class="[
          $style['form-date__check'],
          {[$style['form-date__check--show']]: !validation.$invalid && !customValidation}
        ]"
        xmlns="http://www.w3.org/2000/svg"
        width="100%"
        height="100%"
        viewBox="0 0 48 48"
        enable-background="new 0 0 48 48"><polygon fill="%2300b900" points="40.6,12.1 17,35.7 7.4,26.1 4.6,29 17,41.3 43.4,14.9" /></svg>
    </div>

    <FormValidation
      v-if="(v$ && v$.$invalid) || customValidation"
      class="validation"
      :validation="validation"
      :custom-validation="customValidation" />
  </div>
</template>
<script setup>
import { parseISO, differenceInYears } from 'date-fns'
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
const emits = defineEmits(['update:modelValue', 'blur'])
const props = defineProps({
  label: {
    type: String,
    default: null
  },
  sublabel: {
    type: String,
    default: null
  },
  modelValue: {
    type: [Boolean, String],
    default: null
  },
  validation: {
    type: [Boolean, Object],
    default: null
  },
  validationRules: {
    type: [Object, Array],
    required: false,
    default: null
  }
})

const day = ref(null)
const month = ref(null)
const year = ref(null)
const dayElm = ref(null)
const monthElm = ref(null)
const yearElm = ref(null)
const hasFocus = ref(false)
const customValidation = ref(null)

const rules = {
  day: {
    required: props.validation.required ? required : false
  },
  month: {
    required: props.validation.required ? required : false
  },
  year: {
    required: props.validation.required ? required : false
  }

}

const v$ = useVuelidate(rules, { day, month, year })

const anyEmpty = computed(() => {
  if (!year.value || !month.value || !day.value) {
    return true
  }
})
const onFocus = () => {
  hasFocus.value = true
}
const onBlur = () => {
  if (day.value && month.value && year.value) {
    props.validation.$touch()
  }
  setTimeout(() => {
    if (!hasFocus.value) { emits('blur') }
    if (props.validation && !hasFocus.value) { props.validation.$validate() }
    hasFocus.value = false
  }, 100)
}
const customValidate = () => {
  if (!props.validationRules) { return }

  customValidation.value = {}
  customValidation.value.$dirty = true

  if (props.validationRules.includes('before:18 years ago')) {
    customValidation.value.before18 = props.modelValue && differenceInYears(new Date(), parseISO(props.modelValue)) < 18
  }
  if (props.validationRules.includes('after:120 years ago')) {
    customValidation.value.after120 = props.modelValue && differenceInYears(new Date(), parseISO(props.modelValue)) > 120
  }
}
const emit = () => {
  const leadingZero = number => {
    return ('0' + number).slice(-2)
  }
  const leadingNumber = number => {
    if (number.length !== 2) { return number }
    if (parseInt(number) < 30) {
      return '20' + number
    }
    return '19' + number
  }
  emits('update:modelValue', `${leadingNumber(year.value)}-${leadingZero(month.value)}-${leadingZero(day.value)}`)
}

watch(day, () => {
  if (day.value.length === 2 && (!month.value || month.value.length === 0)) {
    return monthElm.value.focus()
  }

  if (anyEmpty.value) { return emits('update:modelValue', null) }
  emit()
})
watch(month, () => {
  if (month.value.length === 2 && (!year.value || year.value.length === 0) && yearElm.value) {
    yearElm.value.focus()
  }
  if (anyEmpty.value) { return emits('update:modelValue', null) }

  emit()
})
watch(year, () => {
  if (anyEmpty.value) { return emits('update:modelValue', null) }

  if (year.value.length === 4) {
    emit()
  }
})
watch([day, month, year], () => {
  customValidate()
  // if (props.validation && day.value && month.value && year.value) {
  //   props.validation.$validate()
  // }
})

onMounted(() => {
  if (!props.modelValue) { return }
  const dateArray = props.modelValue.split('-')
  day.value = dateArray[2]
  month.value = dateArray[1]
  year.value = dateArray[0]
  customValidate()
})

</script>
<style lang="scss" module>
.form-date {
  &__label {
    color: $grey;
    transform: scale(0.9);
    transform-origin: 0 0;
    margin-bottom: $spacing;

    small {
      font-size: $font-size-xs;
    }
  }

  &__inputs {
    display: flex;
    align-items: center;

    & > * {
      margin-right: $spacing * 0.5;
      width: $spacing * 10;

      &:nth-child(3) {
        width: $spacing * 12;
      }
    }
  }

  &__check {
    color: #00b900;
    margin-left: $spacing;
    width: $spacing * 2;
    opacity: 0;
    transform: translateY(100%);
    transition: all $transition-time $transition-easing;

    &--show {
      opacity: 1;
      transform: translateY(0);
    }
  }
}
</style>
